import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  UserCheck, ArrowLeft, Search, Plus, Phone, Mail, MapPin,
  Edit, Stethoscope, Building2, Shield, Landmark,
  Check, X, LogOut,
  ChevronDown, ChevronUp, Globe, ChevronLeft, ChevronRight,
  Loader, AlertCircle, Archive, Trash2, GitMerge, Copy, Download
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import DuplicateFinderModal from '../components/DuplicateFinderModal';
import MergeClinicModal from '../components/MergeClinicModal';
import SurgeonDuplicateFinderModal from '../components/SurgeonDuplicateFinderModal';
import SurgeonMergeModal from '../components/SurgeonMergeModal';
import UserMenu from '../components/UserMenu';
import { apiFetch } from '../utils/api';


const PAGE_SIZE = 50;

/**
 * Veterinary Surgeons Database — CRUD UI over the four-level referral
 * hierarchy served by /api/surgeons-db:
 *   ownership (corporate groups) → hubs (shared sites) → clinics →
 *   surgeons (people, linked to clinics by clinic_ids + contacts).
 *
 * Tabs switch which collection the table shows; search is debounced
 * (400ms) and server-side. Beyond plain CRUD this page is the data-
 * quality workbench for the imported ezyVet/Simon's data:
 *   - duplicate finders + merge flows for clinics AND surgeons
 *     (see components/*Modal.js)
 *   - bulk maintenance buttons: re-link surgeons↔clinics by email,
 *     parse address strings into structured fields, re-geocode
 *     postcodes — each hitting the matching POST under
 *     /api/surgeons-db/... (see backend/routes/surgeons.py)
 *
 * Cross-page handoff: UKPopulationExplorer navigates here by writing
 * intent into sessionStorage ('surgeons-db-clinic-filter',
 * 'surgeons-db-open-surgeon', 'surgeons-db-return') — a workaround for
 * the app having no router/query params. This page consumes and deletes
 * those keys on mount; the 'returnTo' value restores a Back link to
 * the map.
 */
const SurgeonsDatabase = ({ navigateTo }) => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('surgeons');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterClinicId, setFilterClinicId] = useState(null);
  const [filterHubId, setFilterHubId] = useState(null);
  const [filterOwnershipId, setFilterOwnershipId] = useState(null);
  const [filterTag, setFilterTag] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [expandedSurgeon, setExpandedSurgeon] = useState(null);
  const [page, setPage] = useState(0);
  const [returnTo, setReturnTo] = useState(null); // page to return to (e.g. 'uk-population')
  const [pendingSurgeonId, setPendingSurgeonId] = useState(null); // surgeon to open after data loads

  // ── API data state ────────────────────────────────────────────────────────────
  const [hubs, setHubs] = useState([]);
  const [ownership, setOwnership] = useState([]);
  const [clinicsAll, setClinicsAll] = useState([]);  // lightweight id+name list for dropdowns
  const [items, setItems] = useState([]);             // current page items
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ surgeons_total: 0, surgeons_active: 0, clinics_total: 0, hubs_total: 0, ownership_total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // {id, name, endpoint}
  const [showDupeModal, setShowDupeModal] = useState(false);
  const [dupeMode, setDupeMode] = useState('postcode');
  const [dupeRadius, setDupeRadius] = useState(100);
  const [dupeGroups, setDupeGroups] = useState([]);
  const [dupeLoading, setDupeLoading] = useState(false);
  const [mergeTarget, setMergeTarget] = useState(null); // {keep, discard}
  const [mergeForm, setMergeForm] = useState({});
  // Surgeon duplicates
  const [showSurgeonDupeModal, setShowSurgeonDupeModal] = useState(false);
  const [surgeonDupeGroups, setSurgeonDupeGroups] = useState([]);
  const [surgeonDupeLoading, setSurgeonDupeLoading] = useState(false);
  const [surgeonMergeTarget, setSurgeonMergeTarget] = useState(null);
  const [surgeonMergeForm, setSurgeonMergeForm] = useState({});
  const [clinicSearch, setClinicSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);

  const handleLogout = () => { logout(); navigateTo('home'); };
  const handleFormChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  // ── Debounce search ───────────────────────────────────────────────────────────
  const debounceRef = useRef(null);
  const loadAbortRef = useRef(null);
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchTerm]);

  // ── Load stats + hubs + ownership + clinic list once ────────────────────────
  const loadMeta = useCallback(async () => {
    try {
      const [sRes, hRes, cRes, oRes] = await Promise.all([
        apiFetch(`/api/surgeons-db/stats`),
        apiFetch(`/api/surgeons-db/hubs`),
        apiFetch(`/api/surgeons-db/clinics/all`),
        apiFetch(`/api/surgeons-db/ownership`),
      ]);
      if (sRes.ok) setStats(await sRes.json());
      if (hRes.ok) setHubs(await hRes.json());
      if (cRes.ok) setClinicsAll(await cRes.json());
      if (oRes.ok) setOwnership(await oRes.json());
    } catch (e) {
      setError('Cannot connect to backend. Make sure the server is running.');
    }
  }, []);

  useEffect(() => { loadMeta(); }, [loadMeta]);

  // ── Handle deep-link from UK Population Explorer map ─────────────────────
  useEffect(() => {
    const ret = sessionStorage.getItem('surgeons-db-return');
    if (ret) { sessionStorage.removeItem('surgeons-db-return'); setReturnTo(ret); }

    const clinicId = sessionStorage.getItem('surgeons-db-clinic-filter');
    const clinicName = sessionStorage.getItem('surgeons-db-clinic-name');
    if (clinicId) {
      sessionStorage.removeItem('surgeons-db-clinic-filter');
      sessionStorage.removeItem('surgeons-db-clinic-name');
      setActiveTab('clinics');
      if (clinicName) { setSearchTerm(clinicName); setDebouncedSearch(clinicName); }
    }

    const surgeonId = sessionStorage.getItem('surgeons-db-open-surgeon');
    const surgeonName = sessionStorage.getItem('surgeons-db-open-surgeon-name');
    if (surgeonId) {
      sessionStorage.removeItem('surgeons-db-open-surgeon');
      sessionStorage.removeItem('surgeons-db-open-surgeon-name');
      setActiveTab('surgeons');
      setPendingSurgeonId(surgeonId);
      if (surgeonName) {
        setSearchTerm(surgeonName);
        setDebouncedSearch(surgeonName);
      }
    }
  }, []);

  // ── Load paginated data whenever tab/page/search/filter changes ───────────────
  const loadItems = useCallback(async () => {
    if (loadAbortRef.current) loadAbortRef.current.abort();
    const controller = new AbortController();
    loadAbortRef.current = controller;
    setLoading(true);
    setError(null);
    const skip = page * PAGE_SIZE;
    try {
      let url;
      if (activeTab === 'surgeons') {
        const params = new URLSearchParams({ skip, limit: PAGE_SIZE });
        if (debouncedSearch) params.set('search', debouncedSearch);
        if (filterClinicId) params.set('clinic_id', filterClinicId);
        if (filterHubId) params.set('hub_id', filterHubId);
        if (showArchived) params.set('show_archived', 'true');
        url = `/api/surgeons-db/surgeons?${params}`;
      } else if (activeTab === 'clinics') {
        const params = new URLSearchParams({ skip, limit: PAGE_SIZE });
        if (debouncedSearch) params.set('search', debouncedSearch);
        if (filterHubId) params.set('hub_id', filterHubId);
        if (filterOwnershipId) params.set('ownership_id', filterOwnershipId);
        if (filterTag) params.set('tag', filterTag);
        if (showArchived) params.set('show_archived', 'true');
        url = `/api/surgeons-db/clinics?${params}`;
      } else if (activeTab === 'ownership') {
        url = `/api/surgeons-db/ownership${showArchived ? '?show_archived=true' : ''}`;
      } else {
        url = `/api/surgeons-db/hubs${showArchived ? '?show_archived=true' : ''}`;
      }
      const res = await apiFetch(url, { signal: controller.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (activeTab === 'hubs' || activeTab === 'ownership') {
        setItems(data);
        setTotal(data.length);
      } else {
        setItems(data.items || []);
        setTotal(data.total || 0);
      }
    } catch (e) {
      if (e.name === 'AbortError') return;
      setError('Failed to load data: ' + e.message);
      setItems([]);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [activeTab, page, debouncedSearch, filterClinicId, filterHubId, filterOwnershipId, filterTag, showArchived]);

  useEffect(() => { loadItems(); }, [loadItems]);

  // ── Open pending surgeon (deep-linked from map) once items are loaded ─────────
  useEffect(() => {
    if (!pendingSurgeonId || loading || items.length === 0) return;
    const surgeon = items.find(s => s.id === pendingSurgeonId);
    if (surgeon) {
      setPendingSurgeonId(null);
      openEdit(surgeon);
    }
  }, [pendingSurgeonId, loading, items]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Selection helpers ────────────────────────────────────────────────────────
  const toggleSelect = (id) => setSelectedIds(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  const allSelected = items.length > 0 && items.every(i => selectedIds.has(i.id));
  const someSelected = !allSelected && items.some(i => selectedIds.has(i.id));
  const toggleAll = () => setSelectedIds(allSelected ? new Set() : new Set(items.map(i => i.id)));

  const handleBulkDelete = async () => {
    const endpoint = activeTab === 'surgeons' ? 'surgeons' : activeTab === 'clinics' ? 'clinics' : activeTab === 'ownership' ? 'ownership' : 'hubs';
    await Promise.all([...selectedIds].map(id => apiFetch(`/api/surgeons-db/${endpoint}/${id}`, { method: 'DELETE' })));
    setSelectedIds(new Set());
    setConfirmBulkDelete(false);
    await Promise.all([loadItems(), loadMeta()]);
  };

  const handleExport = async () => {
    try {
      const res = await apiFetch(`/api/surgeons-db/export`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const disposition = res.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : 'vetdb-backup.json';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert('Export failed: ' + e.message); }
  };

  const handleBulkMerge = () => {
    const [id1, id2] = [...selectedIds];
    const item1 = items.find(i => i.id === id1);
    const item2 = items.find(i => i.id === id2);
    if (!item1 || !item2) return;
    if (activeTab === 'clinics') openMerge(item1, item2);
    else if (activeTab === 'surgeons') openSurgeonMerge(item1, item2);
    setSelectedIds(new Set());
  };

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const getSingularLabel = (tab) => ({ surgeons: 'Surgeon', clinics: 'Clinic', hubs: 'Hub', ownership: 'Ownership' }[tab] || tab);
  const getClinicName = (id) => clinicsAll.find(c => c.id === id)?.name || id;
  const getHubName = (id) => hubs.find(h => h.id === id)?.name || id;
  const getOwnershipName = (id) => ownership.find(o => o.id === id)?.name || '—';
  const getHubsForClinic = (clinicId) => {
    const clinic = clinicsAll.find(c => c.id === clinicId);
    return clinic ? (clinic.hub_ids || []).map(hid => hubs.find(h => h.id === hid)).filter(Boolean) : [];
  };

  // ── CRUD handlers ─────────────────────────────────────────────────────────────
  const openAdd = () => {
    setFormData({ contacts: [], clinic_ids: [] });
    setClinicSearch('');
    setShowAddModal(true);
  };

  const openEdit = (item) => {
    setSelectedItem(item);
    setFormData({ ...item });
    setClinicSearch('');
    setShowEditModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const endpoint = activeTab === 'surgeons' ? 'surgeons' : activeTab === 'clinics' ? 'clinics' : activeTab === 'ownership' ? 'ownership' : 'hubs';
      const res = await apiFetch(`/api/surgeons-db/${endpoint}/${selectedItem.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setShowEditModal(false); setSelectedItem(null); setFormData({});
      await Promise.all([loadItems(), loadMeta()]);
    } catch (e) {
      alert('Save failed: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = async () => {
    setSaving(true);
    try {
      const endpoint = activeTab === 'surgeons' ? 'surgeons' : activeTab === 'clinics' ? 'clinics' : activeTab === 'ownership' ? 'ownership' : 'hubs';
      const res = await apiFetch(`/api/surgeons-db/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setShowAddModal(false); setFormData({});
      await Promise.all([loadItems(), loadMeta()]);
    } catch (e) {
      alert('Add failed: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async (id) => {
    const endpoint = activeTab === 'surgeons' ? 'surgeons' : activeTab === 'clinics' ? 'clinics' : activeTab === 'ownership' ? 'ownership' : 'hubs';
    await apiFetch(`/api/surgeons-db/${endpoint}/${id}/archive`, { method: 'PATCH' });
    await Promise.all([loadItems(), loadMeta()]);
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    await apiFetch(`/api/surgeons-db/${confirmDelete.endpoint}/${confirmDelete.id}`, { method: 'DELETE' });
    setConfirmDelete(null);
    await Promise.all([loadItems(), loadMeta()]);
  };

  const openDelete = (item) => {
    const endpoint = activeTab === 'surgeons' ? 'surgeons' : activeTab === 'clinics' ? 'clinics' : activeTab === 'ownership' ? 'ownership' : 'hubs';
    setConfirmDelete({ id: item.id, name: item.name || `${item.first_name} ${item.last_name}`, endpoint });
  };

  const findDuplicates = async () => {
    setDupeLoading(true);
    setDupeGroups([]);
    try {
      const res = await apiFetch(`/api/surgeons-db/clinics/duplicates?mode=${dupeMode}&radius_m=${dupeRadius}`);
      const data = await res.json();
      setDupeGroups(data.groups || []);
    } catch (e) { alert('Failed: ' + e.message); }
    finally { setDupeLoading(false); }
  };

  const openMerge = (keep, discard) => {
    setMergeTarget({ keep, discard });
    setMergeForm({ merged_name: keep.name, merged_address: keep.address || '' });
  };

  const handleMerge = async () => {
    try {
      const res = await apiFetch(`/api/surgeons-db/clinics/merge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ keep_id: mergeTarget.keep.id, discard_id: mergeTarget.discard.id, ...mergeForm }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setMergeTarget(null);
      await findDuplicates();
      await Promise.all([loadItems(), loadMeta()]);
    } catch (e) { alert('Merge failed: ' + e.message); }
  };

  const handleRelinkClinics = async () => {
    if (!window.confirm('Re-link all surgeons to clinics by matching email addresses?\n\nThis will only update surgeons with no clinic links. Use the overwrite option in the API if you need to force-update all.')) return;
    try {
      const res = await apiFetch(`/api/surgeons-db/surgeons/relink-clinics`, { method: 'POST' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      alert(`Re-link complete:\n\u2713 Linked: ${data.linked}\n\u2014 No email: ${data.skipped_no_email}\n\u2717 No match found: ${data.unlinked}`);
      await loadItems();
    } catch (e) { alert('Re-link failed: ' + e.message); }
  };

  const handleParseAddresses = async () => {
    if (!window.confirm('Parse all existing address strings into structured fields (line 1, line 2, city, postcode)?\n\nThis will only update clinics that do not already have structured address fields set.')) return;
    try {
      const res = await apiFetch(`/api/surgeons-db/clinics/parse-addresses`, { method: 'POST' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      alert(`Address parse complete:\n✓ Parsed: ${data.processed}\n— No address to parse: ${data.skipped_no_address}`);
      await loadItems();
    } catch (e) { alert('Parse failed: ' + e.message); }
  };

  const handleRegeocode = async () => {
    if (!window.confirm('Re-geocode all clinics from their postcode field? This will update lat/lng in the database for all clinics that have a postcode. Clinics without a postcode keep their existing coordinates.')) return;
    try {
      const res = await apiFetch(`/api/surgeons-db/clinics/regeocode`, { method: 'POST' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      alert(`Re-geocode complete:\n✓ Updated: ${data.updated}\n— No postcode: ${data.skipped_no_postcode}\n✗ Failed lookup: ${data.failed_geocode}`);
      await loadItems();
    } catch (e) { alert('Re-geocode failed: ' + e.message); }
  };

  const findSurgeonDuplicates = async () => {
    setSurgeonDupeLoading(true);
    setSurgeonDupeGroups([]);
    try {
      const res = await apiFetch(`/api/surgeons-db/surgeons/duplicates`);
      const data = await res.json();
      setSurgeonDupeGroups(data.groups || []);
    } catch (e) { alert('Failed: ' + e.message); }
    finally { setSurgeonDupeLoading(false); }
  };

  const openSurgeonMerge = (keep, discard) => {
    setSurgeonMergeTarget({ keep, discard });
    setSurgeonMergeForm({
      merged_first_name: keep.first_name || '',
      merged_last_name: keep.last_name || '',
      merged_specialty: keep.specialty || discard.specialty || '',
      merged_qualifications: keep.qualifications || discard.qualifications || '',
    });
  };

  const handleSurgeonMerge = async () => {
    try {
      const res = await apiFetch(`/api/surgeons-db/surgeons/merge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          keep_id: surgeonMergeTarget.keep.id,
          discard_id: surgeonMergeTarget.discard.id,
          ...surgeonMergeForm,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSurgeonMergeTarget(null);
      await findSurgeonDuplicates();
      await Promise.all([loadItems(), loadMeta()]);
    } catch (e) { alert('Merge failed: ' + e.message); }
  };

  const toggleContactMarketing = async (surgeon, contactId) => {
    const updated = {
      ...surgeon,
      contacts: surgeon.contacts.map(c =>
        c.id !== contactId ? c : { ...c, marketing_opt_in: !c.marketing_opt_in }
      ),
    };
    try {
      await apiFetch(`/api/surgeons-db/surgeons/${surgeon.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updated),
      });
      setItems(prev => prev.map(s => s.id === surgeon.id ? updated : s));
    } catch (e) { /* silent */ }
  };

  // ── Contact form helpers ─────────────────────────────────────────────────────
  const addContact = () => {
    const newContact = { id: `new-${Date.now()}`, type: 'email', value: '', context: 'work', clinic_id: null, marketing_opt_in: true };
    setFormData(prev => ({ ...prev, contacts: [...(prev.contacts || []), newContact] }));
  };
  const removeContact = (contactId) => {
    setFormData(prev => ({ ...prev, contacts: prev.contacts.filter(c => c.id !== contactId) }));
  };
  const updateContact = (contactId, field, value) => {
    setFormData(prev => ({ ...prev, contacts: prev.contacts.map(c => c.id !== contactId ? c : { ...c, [field]: value }) }));
  };
  const toggleClinicInForm = (clinicId) => {
    const ids = formData.clinic_ids || [];
    const removing = ids.includes(clinicId);
    if (removing) {
      // Find ownership of this clinic
      const clinic = clinicsAll.find(c => c.id === clinicId);
      const ownershipId = clinic?.ownership_id || null;
      // After removal, which other clinics will still be linked under the same ownership?
      const remainingIds = ids.filter(id => id !== clinicId);
      const ownershipStillLinked = ownershipId && remainingIds.some(id => {
        const c = clinicsAll.find(x => x.id === id);
        return c?.ownership_id === ownershipId;
      });
      setFormData(prev => ({
        ...prev,
        clinic_ids: remainingIds,
        // Remove contacts linked to this clinic; also remove ownership contacts if no more clinics under that owner
        contacts: (prev.contacts || []).filter(c => {
          if (c.clinic_id === clinicId) return false;
          if (c.ownership_id === ownershipId && !ownershipStillLinked) return false;
          return true;
        }),
      }));
    } else {
      setFormData(prev => ({ ...prev, clinic_ids: [...ids, clinicId] }));
    }
  };

  const addEmailForClinic = (clinicId) => {
    const newContact = { id: `new-${Date.now()}`, type: 'email', value: '', context: 'work', clinic_id: clinicId, ownership_id: null, marketing_opt_in: true };
    setFormData(prev => ({ ...prev, contacts: [...(prev.contacts || []), newContact] }));
  };

  const addEmailForOwnership = (ownershipId) => {
    const newContact = { id: `new-${Date.now()}`, type: 'email', value: '', context: 'work', clinic_id: null, ownership_id: ownershipId, marketing_opt_in: true };
    setFormData(prev => ({ ...prev, contacts: [...(prev.contacts || []), newContact] }));
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const tagClass = (tag) => tag === 'ezyvet' ? 'text-xs px-1.5 py-0 rounded-full font-medium border bg-blue-50 text-blue-600 border-blue-200' : tag === 'simons' ? 'text-xs px-1.5 py-0 rounded-full font-medium border bg-green-50 text-green-600 border-green-200' : 'text-xs px-1.5 py-0 rounded-full font-medium border bg-gray-50 text-gray-500 border-gray-200';
  const tagLabel = (tag) => tag === 'ezyvet' ? 'EzyVet' : tag === 'simons' ? "Simon's" : tag;

  const OWNERSHIP_TYPE_COLORS = {
    independent: 'text-green-700 bg-green-100',
    corporate:   'text-blue-700 bg-blue-100',
    charity:     'text-purple-700 bg-purple-100',
    unknown:     'text-gray-600 bg-gray-100',
  };

  const CLINIC_TYPES = [
    { value: 'small_animal', label: 'Small Animal' },
    { value: 'referral',     label: 'Referral' },
    { value: 'equine',       label: 'Equine' },
    { value: 'mixed',        label: 'Mixed' },
    { value: 'exotic',       label: 'Exotic' },
    { value: 'farm',         label: 'Farm' },
    { value: 'emergency',    label: 'Emergency' },
    { value: 'specialist',   label: 'Specialist' },
  ];
  const CLINIC_TYPE_COLORS = {
    small_animal: 'text-green-700 bg-green-100',
    referral:     'text-blue-700 bg-blue-100',
    equine:       'text-amber-700 bg-amber-100',
    mixed:        'text-teal-700 bg-teal-100',
    exotic:       'text-purple-700 bg-purple-100',
    farm:         'text-orange-700 bg-orange-100',
    emergency:    'text-red-700 bg-red-100',
    specialist:   'text-indigo-700 bg-indigo-100',
  };

  const tabs = [
    { id: 'surgeons',  label: 'Surgeons',  icon: Stethoscope, count: stats.surgeons_active },
    { id: 'clinics',   label: 'Clinics',   icon: Building2,   count: stats.clinics_total },
    { id: 'hubs',      label: 'Hubs',      icon: Shield,      count: stats.hubs_total },
    { id: 'ownership', label: 'Ownership', icon: Landmark,    count: stats.ownership_total },
  ];

  const ContactRow = ({ contact, surgeon, editable = false, onUpdate, onRemove }) => {
    const Icon = contact.type === 'email' ? Mail : Phone;
    const clinicName = contact.clinic_id ? getClinicName(contact.clinic_id) : null;
    const ownershipName = contact.ownership_id ? ownership.find(o => o.id === contact.ownership_id)?.name : null;
    const contextLabel = clinicName ? clinicName : ownershipName ? `${ownershipName} (all clinics)` : contact.context;
    return (
      <div className={`flex items-center space-x-2 text-sm py-1 ${editable ? 'border-b border-gray-100 last:border-0' : ''}`}>
        <Icon className="h-3.5 w-3.5 text-gray-400 shrink-0" />
        {editable ? (
          <>
            <input value={contact.value} onChange={e => onUpdate(contact.id, 'value', e.target.value)} placeholder="email address" className="flex-1 text-xs border border-gray-200 rounded px-1.5 py-0.5 min-w-0" />
            <button type="button" onClick={() => onUpdate(contact.id, 'marketing_opt_in', !contact.marketing_opt_in)} className={`text-xs px-1.5 py-0.5 rounded font-medium shrink-0 ${contact.marketing_opt_in ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
              {contact.marketing_opt_in ? 'Opt In' : 'Opt Out'}
            </button>
            <button type="button" onClick={() => onRemove(contact.id)} className="text-red-400 hover:text-red-600 shrink-0"><X className="h-3.5 w-3.5" /></button>
          </>
        ) : (
          <>
            <span className="text-gray-800 flex-1 truncate">{contact.value}</span>
            <span className="text-xs text-gray-400 shrink-0">{contextLabel}</span>
            <button
              onClick={() => surgeon && toggleContactMarketing(surgeon, contact.id)}
              className={`text-xs px-1.5 py-0.5 rounded font-medium shrink-0 ${contact.marketing_opt_in ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}
              title={`Marketing: ${contact.marketing_opt_in ? 'Opted In' : 'Opted Out'}`}
            >
              {contact.marketing_opt_in ? 'In' : 'Out'}
            </button>
          </>
        )}
      </div>
    );
  };

  // Phone-only ContactRow for the read view
  const PhoneRow = ({ contact }) => (
    <div className="flex items-center space-x-2 text-sm py-1">
      <Phone className="h-3.5 w-3.5 text-gray-400 shrink-0" />
      <span className="text-gray-800 flex-1 truncate">{contact.value}</span>
      <span className="text-xs text-gray-400 shrink-0">mobile</span>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <button onClick={() => navigateTo('staff-portal')} className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                <UserCheck className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Veterinary Surgeons Database</h1>
                <p className="text-sm text-gray-500">Surgeons · Clinics · Hubs</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {returnTo === 'uk-population' && (
                <button
                  onClick={() => navigateTo('uk-population')}
                  className="flex items-center space-x-2 px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded-lg text-sm font-medium"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span>Return to Population Explorer</span>
                </button>
              )}
              <button onClick={handleExport} title="Download full database backup as JSON" className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-900 border border-gray-200 hover:border-gray-300 rounded-lg bg-white text-sm font-medium">
                <Download className="h-4 w-4" />
                <span>Export</span>
              </button>
              <button onClick={openAdd} className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg">
                <Plus className="h-4 w-4" />
                <span>Add {getSingularLabel(activeTab)}</span>
              </button>
              <button onClick={handleLogout} className="flex items-center space-x-2 px-4 py-2 text-gray-700 hover:text-gray-900">
                <LogOut className="h-4 w-4" /><span>Logout</span>
              </button>
              <UserMenu navigateTo={navigateTo} />
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

        {/* Summary Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Surgeons',  count: stats.surgeons_active,  icon: Stethoscope, color: 'green',  tab: 'surgeons' },
            { label: 'Clinics',   count: stats.clinics_total,    icon: Building2,   color: 'blue',   tab: 'clinics' },
            { label: 'Hubs',      count: stats.hubs_total,       icon: Shield,      color: 'purple', tab: 'hubs' },
            { label: 'Ownership', count: stats.ownership_total,  icon: Landmark,    color: 'orange', tab: 'ownership' },
          ].map(stat => (
            <button key={stat.tab} onClick={() => { setActiveTab(stat.tab); setFilterClinicId(null); setFilterHubId(null); setFilterOwnershipId(null); setSelectedIds(new Set()); }}
              className={`bg-white rounded-xl shadow-sm border-2 p-5 flex items-center justify-between transition-all ${activeTab === stat.tab ? `border-${stat.color}-500` : 'border-gray-200 hover:border-gray-300'}`}>
              <div>
                <p className="text-sm text-gray-500">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.count}</p>
              </div>
              <div className={`w-12 h-12 rounded-lg bg-${stat.color}-100 flex items-center justify-center`}>
                <stat.icon className={`h-6 w-6 text-${stat.color}-600`} />
              </div>
            </button>
          ))}
        </div>

        {/* Tabs + Search bar */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
          <div className="flex items-center justify-between px-6 pt-4">
            <div className="flex space-x-1">
              {tabs.map(tab => (
                <button key={tab.id} onClick={() => { setActiveTab(tab.id); setFilterClinicId(null); setFilterHubId(null); setFilterOwnershipId(null); setSearchTerm(''); setSelectedIds(new Set()); }}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === tab.id ? 'bg-green-50 text-green-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}>
                  <tab.icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                  <span className={`px-1.5 py-0.5 rounded-full text-xs ${activeTab === tab.id ? 'bg-green-200 text-green-800' : 'bg-gray-100 text-gray-600'}`}>{tab.count}</span>
                </button>
              ))}
            </div>
            <button onClick={() => setShowArchived(v => !v)}
              className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${showArchived ? 'bg-amber-100 text-amber-800 border-amber-300' : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'}`}>
              <Archive className="h-3.5 w-3.5" />
              <span>{showArchived ? 'Showing Archived' : 'Show Archived'}</span>
            </button>
          </div>

          {/* Active filters */}
          {(filterClinicId || filterHubId || filterOwnershipId || filterTag) && (
            <div className="flex items-center flex-wrap gap-2 px-6 py-2 border-t border-gray-100 bg-blue-50">
              <span className="text-xs text-blue-600 font-medium">Filtered by:</span>
              {filterClinicId && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full flex items-center space-x-1">
                <Building2 className="h-3 w-3" /><span>{getClinicName(filterClinicId)}</span>
                <button onClick={() => setFilterClinicId(null)}><X className="h-3 w-3 ml-1" /></button>
              </span>}
              {filterHubId && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full flex items-center space-x-1">
                <Shield className="h-3 w-3" /><span>{getHubName(filterHubId)}</span>
                <button onClick={() => setFilterHubId(null)}><X className="h-3 w-3 ml-1" /></button>
              </span>}
              {filterOwnershipId && <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full flex items-center space-x-1">
                <Landmark className="h-3 w-3" /><span>{getOwnershipName(filterOwnershipId)}</span>
                <button onClick={() => setFilterOwnershipId(null)}><X className="h-3 w-3 ml-1" /></button>
              </span>}
              {filterTag && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex items-center space-x-1">
                <span>🏷 {filterTag}</span>
                <button onClick={() => setFilterTag(null)}><X className="h-3 w-3 ml-1" /></button>
              </span>}
            </div>
          )}

          <div className="px-6 pb-4 pt-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                placeholder={`Search ${activeTab} by name, address, contact…`}
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent" />
            </div>
          </div>
        </div>

        {/* Error banner */}
        {error && (
          <div className="mb-4 flex items-center space-x-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            <AlertCircle className="h-4 w-4 shrink-0" /><span className="text-sm">{error}</span>
          </div>
        )}

        {/* ── SURGEONS TAB ───────────────────────────────────────────────── */}
        {activeTab === 'surgeons' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-4 py-2 border-b border-gray-100 flex items-center justify-end space-x-2">
              <button onClick={handleRelinkClinics}
                className="flex items-center space-x-1 px-2.5 py-0.5 rounded-lg border border-blue-300 bg-blue-50 text-blue-700 text-xs font-medium hover:bg-blue-100">
                <Building2 className="h-3 w-3" /><span>Re-link Clinics</span>
              </button>
              <button onClick={() => { setShowSurgeonDupeModal(true); setSurgeonDupeGroups([]); }}
                className="flex items-center space-x-1 px-2.5 py-0.5 rounded-lg border border-amber-300 bg-amber-50 text-amber-700 text-xs font-medium hover:bg-amber-100">
                <Copy className="h-3 w-3" /><span>Find Duplicates</span>
              </button>
            </div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 w-8">
                    <input type="checkbox" checked={allSelected} ref={el => { if (el) el.indeterminate = someSelected; }} onChange={toggleAll} className="rounded border-gray-300 text-green-600 focus:ring-green-500" />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Surgeon</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Specialty / Qualifications</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Clinics</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Contacts</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading && items.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-12 text-gray-400"><Loader className="h-6 w-6 animate-spin mx-auto" /></td></tr>
                )}
                {items.map(surgeon => (
                  <React.Fragment key={surgeon.id}>
                    <tr className={`hover:bg-gray-50 ${selectedIds.has(surgeon.id) ? 'bg-green-50' : surgeon.archived ? 'bg-amber-50 opacity-75' : ''}`}>
                      <td className="px-3 py-4">
                        <input type="checkbox" checked={selectedIds.has(surgeon.id)} onChange={() => toggleSelect(surgeon.id)} className="rounded border-gray-300 text-green-600 focus:ring-green-500" />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${surgeon.archived ? 'bg-amber-100' : 'bg-green-100'}`}>
                            <Stethoscope className={`h-5 w-5 ${surgeon.archived ? 'text-amber-600' : 'text-green-600'}`} />
                          </div>
                          <div>
                            <div className="flex items-center space-x-1.5">
                              <span className="text-sm font-semibold text-gray-900">{surgeon.title} {surgeon.first_name} {surgeon.last_name}</span>
                              {surgeon.archived && <span className="text-xs px-1.5 py-0 rounded-full bg-amber-100 text-amber-700 font-medium border border-amber-200">Archived</span>}
                            </div>
                            <div className="text-xs text-gray-400">{surgeon.id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{surgeon.specialty}</div>
                        <div className="text-xs text-gray-400">{surgeon.qualifications}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          {(surgeon.clinic_ids || []).map(cid => {
                            const clinicHubList = getHubsForClinic(cid);
                            return (
                              <div key={cid} className="flex items-center space-x-1">
                                <button onClick={() => { setFilterClinicId(cid); setActiveTab('surgeons'); }} className="text-xs text-blue-600 hover:underline">{getClinicName(cid)}</button>
                                {clinicHubList.length > 0 && <span className="text-xs text-gray-400">· {clinicHubList.map(h => h.name).join(', ')}</span>}
                              </div>
                            );
                          })}
                        </div>
                      </td>
                      <td className="px-6 py-4 max-w-xs">
                        <div className="space-y-0.5">
                          {(surgeon.contacts || []).filter(c => c.type === 'phone').map(c => (
                            <PhoneRow key={c.id} contact={c} />
                          ))}
                          {(surgeon.clinic_ids || []).map(cid => {
                            const clinic = clinicsAll.find(x => x.id === cid);
                            if (!clinic?.phone) return null;
                            return (
                              <div key={`cp-${cid}`} className="flex items-center space-x-2 text-sm py-1">
                                <Phone className="h-3.5 w-3.5 text-gray-300 shrink-0" />
                                <span className="text-gray-400 flex-1 truncate">{clinic.phone}</span>
                                <span className="text-xs text-gray-300 shrink-0 truncate max-w-[7rem]">{clinic.name}</span>
                              </div>
                            );
                          })}
                          {(surgeon.contacts || []).filter(c => c.type === 'email').map(c => (
                            <ContactRow key={c.id} contact={c} surgeon={surgeon} />
                          ))}
                          {(surgeon.clinic_ids || []).map(cid => {
                            const clinic = clinicsAll.find(x => x.id === cid);
                            if (!clinic?.email) return null;
                            return (
                              <div key={`ce-${cid}`} className="flex items-center space-x-2 text-sm py-1">
                                <Mail className="h-3.5 w-3.5 text-gray-300 shrink-0" />
                                <span className="text-gray-400 flex-1 truncate">{clinic.email}</span>
                                <span className="text-xs text-gray-300 shrink-0 truncate max-w-[7rem]">{clinic.name}</span>
                              </div>
                            );
                          })}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-1">
                          <button onClick={() => openEdit(surgeon)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg" title="Edit"><Edit className="h-4 w-4" /></button>
                          <button onClick={() => setExpandedSurgeon(expandedSurgeon === surgeon.id ? null : surgeon.id)} className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg" title="Notes">{expandedSurgeon === surgeon.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</button>
                          <button onClick={() => handleArchive(surgeon.id)} className={`p-1.5 rounded-lg ${surgeon.archived ? 'text-amber-600 bg-amber-50' : 'text-gray-400 hover:bg-gray-100'}`} title={surgeon.archived ? 'Unarchive' : 'Archive'}><Archive className="h-4 w-4" /></button>
                          <button onClick={() => openDelete(surgeon)} className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg" title="Delete"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      </td>
                    </tr>
                    {expandedSurgeon === surgeon.id && surgeon.notes && (
                      <tr className="bg-gray-50">
                        <td colSpan={6} className="px-6 py-3 text-sm text-gray-600 italic">{surgeon.notes}</td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            {!loading && items.length === 0 && (
              <div className="text-center py-12 text-gray-400">No surgeons found</div>
            )}
          </div>
        )}

        {/* ── CLINICS TAB ────────────────────────────────────────────────── */}
        {activeTab === 'clinics' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-4 py-2 border-b border-gray-100 flex items-center space-x-2">
              <span className="text-xs text-gray-500 font-medium">Source:</span>
              {['ezyvet', 'simons'].map(tag => (
                <button key={tag} onClick={() => setFilterTag(filterTag === tag ? null : tag)}
                  className={`px-2.5 py-0.5 rounded-full text-xs font-medium border transition-colors ${
                    filterTag === tag ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-600 border-gray-300 hover:border-green-400'
                  }`}>
                  {tag === 'ezyvet' ? 'EzyVet' : "Simon's"}
                </button>
              ))}
              {filterTag && <button onClick={() => setFilterTag(null)} className="text-xs text-gray-400 hover:text-gray-600 ml-1">Clear</button>}
              <div className="ml-auto flex items-center space-x-2">
                <button onClick={handleParseAddresses} className="flex items-center space-x-1 px-2.5 py-0.5 rounded-lg border border-purple-300 bg-purple-50 text-purple-700 text-xs font-medium hover:bg-purple-100">
                  <MapPin className="h-3 w-3" /><span>Parse Addresses</span>
                </button>
                <button onClick={handleRegeocode} className="flex items-center space-x-1 px-2.5 py-0.5 rounded-lg border border-blue-300 bg-blue-50 text-blue-700 text-xs font-medium hover:bg-blue-100">
                  <MapPin className="h-3 w-3" /><span>Re-geocode All</span>
                </button>
                <button onClick={() => { setShowDupeModal(true); setDupeGroups([]); }} className="flex items-center space-x-1 px-2.5 py-0.5 rounded-lg border border-amber-300 bg-amber-50 text-amber-700 text-xs font-medium hover:bg-amber-100">
                  <Copy className="h-3 w-3" /><span>Find Duplicates</span>
                </button>
              </div>
            </div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 w-8">
                    <input type="checkbox" checked={allSelected} ref={el => { if (el) el.indeterminate = someSelected; }} onChange={toggleAll} className="rounded border-gray-300 text-green-600 focus:ring-green-500" />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Clinic</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Hub</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Ownership</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Address</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Surgeons</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading && items.length === 0 && (
                  <tr><td colSpan={8} className="text-center py-12 text-gray-400"><Loader className="h-6 w-6 animate-spin mx-auto" /></td></tr>
                )}
                {items.map(clinic => {
                  const clinicHubs = (clinic.hub_ids || []).map(hid => hubs.find(h => h.id === hid)).filter(Boolean);
                  const typeLabel = CLINIC_TYPES.find(t => t.value === clinic.clinic_type)?.label;
                  const typeColor = CLINIC_TYPE_COLORS[clinic.clinic_type] || 'text-gray-600 bg-gray-100';
                  return (
                    <tr key={clinic.id} className={`hover:bg-gray-50 ${selectedIds.has(clinic.id) ? 'bg-green-50' : clinic.archived ? 'bg-amber-50 opacity-75' : ''}`}>
                      <td className="px-3 py-4">
                        <input type="checkbox" checked={selectedIds.has(clinic.id)} onChange={() => toggleSelect(clinic.id)} className="rounded border-gray-300 text-green-600 focus:ring-green-500" />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${clinic.archived ? 'bg-amber-100' : 'bg-blue-100'}`}>
                            <Building2 className={`h-5 w-5 ${clinic.archived ? 'text-amber-600' : 'text-blue-600'}`} />
                          </div>
                          <div>
                            <div className="flex items-center space-x-1.5">
                              <span className="text-sm font-semibold text-gray-900">{clinic.name}</span>
                              {clinic.archived && <span className="text-xs px-1.5 rounded-full bg-amber-100 text-amber-700 font-medium border border-amber-200">Archived</span>}
                            </div>
                            <div className="flex items-center gap-1 mt-0.5">
                              <span className="text-xs text-gray-400">{clinic.id}</span>
                              {(clinic.tags || []).map(tag => (
                                <button key={tag} onClick={() => setFilterTag(tag)} className={tagClass(tag)}>
                                  {tagLabel(tag)}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {typeLabel
                          ? <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${typeColor}`}>{typeLabel}</span>
                          : <span className="text-sm text-gray-400">—</span>}
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          {clinicHubs.length > 0 ? clinicHubs.map(hub => (
                            <button key={hub.id} onClick={() => setFilterHubId(hub.id)}
                              className="text-sm text-purple-600 hover:underline block">
                              {hub.name}
                            </button>
                          )) : <span className="text-sm text-gray-400">—</span>}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {clinic.ownership_id ? (
                          <button onClick={() => setFilterOwnershipId(clinic.ownership_id)}
                            className="text-sm text-orange-600 hover:underline flex items-center space-x-1">
                            <Landmark className="h-3 w-3" />
                            <span>{getOwnershipName(clinic.ownership_id)}</span>
                          </button>
                        ) : <span className="text-sm text-gray-400">—</span>}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-start space-x-1 text-sm text-gray-600">
                          <MapPin className="h-3.5 w-3.5 text-gray-400 mt-0.5 shrink-0" />
                          <span>{clinic.address}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => { setFilterClinicId(clinic.id); setActiveTab('surgeons'); setFilterHubId(null); setFilterOwnershipId(null); }}
                          className="flex items-center space-x-1 text-sm font-semibold text-green-700 hover:text-green-900 hover:underline">
                          <Stethoscope className="h-3.5 w-3.5" />
                          <span>{clinic.surgeon_count ?? '—'}</span>
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-1">
                          <button onClick={() => openEdit(clinic)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg" title="Edit"><Edit className="h-4 w-4" /></button>
                          <button onClick={() => handleArchive(clinic.id)} className={`p-1.5 rounded-lg ${clinic.archived ? 'text-amber-600 bg-amber-50' : 'text-gray-400 hover:bg-gray-100'}`} title={clinic.archived ? 'Unarchive' : 'Archive'}><Archive className="h-4 w-4" /></button>
                          <button onClick={() => openDelete(clinic)} className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg" title="Delete"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {!loading && items.length === 0 && (
              <div className="text-center py-12 text-gray-400">No clinics found</div>
            )}
          </div>
        )}

        {/* ── HUBS TAB ─────────────────────────────────────────────────── */}
        {activeTab === 'hubs' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 w-8">
                    <input type="checkbox" checked={allSelected} ref={el => { if (el) el.indeterminate = someSelected; }} onChange={toggleAll} className="rounded border-gray-300 text-green-600 focus:ring-green-500" />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Hub</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Address</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Website</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Notes</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Clinics</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map(hub => {
                  const clinicCount = clinicsAll.filter(c => (c.hub_ids || []).includes(hub.id)).length;
                  return (
                    <tr key={hub.id} className={`hover:bg-gray-50 ${selectedIds.has(hub.id) ? 'bg-green-50' : hub.archived ? 'bg-amber-50 opacity-75' : ''}`}>
                      <td className="px-3 py-4">
                        <input type="checkbox" checked={selectedIds.has(hub.id)} onChange={() => toggleSelect(hub.id)} className="rounded border-gray-300 text-green-600 focus:ring-green-500" />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${hub.archived ? 'bg-amber-100' : 'bg-purple-100'}`}>
                            <Shield className={`h-5 w-5 ${hub.archived ? 'text-amber-600' : 'text-purple-600'}`} />
                          </div>
                          <div>
                            <div className="flex items-center space-x-1.5">
                              <span className="text-sm font-semibold text-gray-900">{hub.name}</span>
                              {hub.archived && <span className="text-xs px-1.5 rounded-full bg-amber-100 text-amber-700 font-medium border border-amber-200">Archived</span>}
                            </div>
                            <div className="text-xs text-gray-400">{hub.id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{hub.address || '—'}</td>
                      <td className="px-6 py-4">
                        {hub.website ? (
                          <a href={hub.website} target="_blank" rel="noreferrer" className="flex items-center space-x-1 text-sm text-blue-600 hover:underline">
                            <Globe className="h-3.5 w-3.5" /><span>Website</span>
                          </a>
                        ) : <span className="text-sm text-gray-400">—</span>}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 max-w-xs">{hub.notes || '—'}</td>
                      <td className="px-6 py-4">
                        <button onClick={() => { setFilterHubId(hub.id); setActiveTab('clinics'); }}
                          className="text-sm text-blue-600 hover:underline">
                          {clinicCount} clinic{clinicCount !== 1 ? 's' : ''}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-1">
                          <button onClick={() => openEdit(hub)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg" title="Edit"><Edit className="h-4 w-4" /></button>
                          <button onClick={() => handleArchive(hub.id)} className={`p-1.5 rounded-lg ${hub.archived ? 'text-amber-600 bg-amber-50' : 'text-gray-400 hover:bg-gray-100'}`} title={hub.archived ? 'Unarchive' : 'Archive'}><Archive className="h-4 w-4" /></button>
                          <button onClick={() => openDelete(hub)} className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg" title="Delete"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {items.length === 0 && (
              <div className="text-center py-12 text-gray-400">No hubs found</div>
            )}
          </div>
        )}
        {/* ── OWNERSHIP TAB ─────────────────────────────────────────────────── */}
        {activeTab === 'ownership' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 w-8">
                    <input type="checkbox" checked={allSelected} ref={el => { if (el) el.indeterminate = someSelected; }} onChange={toggleAll} className="rounded border-gray-300 text-green-600 focus:ring-green-500" />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Website</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Notes</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Clinics</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map(own => {
                  const clinicCount = own.clinic_count ?? 0;
                  return (
                    <tr key={own.id} className={`hover:bg-gray-50 ${selectedIds.has(own.id) ? 'bg-green-50' : own.archived ? 'bg-amber-50 opacity-75' : ''}`}>
                      <td className="px-3 py-4">
                        <input type="checkbox" checked={selectedIds.has(own.id)} onChange={() => toggleSelect(own.id)} className="rounded border-gray-300 text-green-600 focus:ring-green-500" />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${own.archived ? 'bg-amber-100' : 'bg-orange-100'}`}>
                            <Landmark className={`h-5 w-5 ${own.archived ? 'text-amber-600' : 'text-orange-600'}`} />
                          </div>
                          <div>
                            <div className="flex items-center space-x-1.5">
                              <span className="text-sm font-semibold text-gray-900">{own.name}</span>
                              {own.archived && <span className="text-xs px-1.5 rounded-full bg-amber-100 text-amber-700 font-medium border border-amber-200">Archived</span>}
                            </div>
                            <div className="text-xs text-gray-400">{own.id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${OWNERSHIP_TYPE_COLORS[own.type] || OWNERSHIP_TYPE_COLORS.unknown}`}>
                          {own.type || 'unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {own.website ? (
                          <a href={own.website} target="_blank" rel="noreferrer" className="flex items-center space-x-1 text-sm text-blue-600 hover:underline">
                            <Globe className="h-3.5 w-3.5" /><span>Website</span>
                          </a>
                        ) : <span className="text-sm text-gray-400">—</span>}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">{own.notes || '—'}</td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => { setFilterOwnershipId(own.id); setActiveTab('clinics'); }}
                          className="text-sm text-blue-600 hover:underline"
                        >
                          {clinicCount} clinic{clinicCount !== 1 ? 's' : ''}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-1">
                          <button onClick={() => openEdit(own)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg" title="Edit"><Edit className="h-4 w-4" /></button>
                          <button onClick={() => handleArchive(own.id)} className={`p-1.5 rounded-lg ${own.archived ? 'text-amber-600 bg-amber-50' : 'text-gray-400 hover:bg-gray-100'}`} title={own.archived ? 'Unarchive' : 'Archive'}><Archive className="h-4 w-4" /></button>
                          <button onClick={() => openDelete(own)} className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg" title="Delete"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {items.length === 0 && (
              <div className="text-center py-12 text-gray-400">No ownership entries found</div>
            )}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && activeTab !== 'hubs' && activeTab !== 'ownership' && (
          <div className="flex items-center justify-between mt-4">
            <span className="text-sm text-gray-500">
              Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total.toLocaleString()}
            </span>
            <div className="flex items-center space-x-2">
              <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                className="p-1.5 rounded-lg border border-gray-300 disabled:opacity-40 hover:bg-gray-50">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="text-sm text-gray-700">Page {page + 1} of {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
                className="p-1.5 rounded-lg border border-gray-300 disabled:opacity-40 hover:bg-gray-50">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

      </div>

      {/* ── BULK ACTION BAR ─────────────────────────────────────────────────── */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center space-x-3 bg-gray-900 text-white px-5 py-3 rounded-2xl shadow-2xl">
          <span className="text-sm font-medium">{selectedIds.size} selected</span>
          <div className="w-px h-4 bg-gray-600" />
          {selectedIds.size === 2 && (activeTab === 'clinics' || activeTab === 'surgeons') && (
            <button onClick={handleBulkMerge}
              className="flex items-center space-x-1.5 px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-white text-sm font-medium transition-colors">
              <GitMerge className="h-4 w-4" /><span>Merge</span>
            </button>
          )}
          <button onClick={() => setConfirmBulkDelete(true)}
            className="flex items-center space-x-1.5 px-3 py-1.5 rounded-lg bg-red-500 hover:bg-red-400 text-white text-sm font-medium transition-colors">
            <Trash2 className="h-4 w-4" /><span>Delete {selectedIds.size}</span>
          </button>
          <button onClick={() => setSelectedIds(new Set())}
            className="p-1.5 rounded-lg hover:bg-gray-700 text-gray-400 hover:text-white transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* ── CONFIRM BULK DELETE ──────────────────────────────────────────────── */}
      {confirmBulkDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete {selectedIds.size} {getSingularLabel(activeTab)}{selectedIds.size !== 1 ? 's' : ''}?</h3>
            <p className="text-sm text-gray-500 mb-6">This cannot be undone.</p>
            <div className="flex space-x-3">
              <button onClick={() => setConfirmBulkDelete(false)} className="flex-1 px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium hover:bg-gray-50">Cancel</button>
              <button onClick={handleBulkDelete} className="flex-1 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* ── ADD / EDIT MODAL ───────────────────────────────────────────────── */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-6 border-b border-gray-200 shrink-0">
              <h2 className="text-lg font-bold text-gray-900">
                {showEditModal ? 'Edit' : 'Add'} {getSingularLabel(activeTab)}
              </h2>
              <button onClick={() => { setShowAddModal(false); setShowEditModal(false); setFormData({}); }} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {/* SURGEON FORM */}
              {activeTab === 'surgeons' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                      <select value={formData.title || ''} onChange={e => handleFormChange('title', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500">
                        <option value="">—</option>
                        {['Mr','Mrs','Ms','Dr','Prof'].map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Specialty</label>
                      <input value={formData.specialty || ''} onChange={e => handleFormChange('specialty', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500" placeholder="e.g. Small Animal Surgery" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                      <input value={formData.first_name || ''} onChange={e => handleFormChange('first_name', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                      <input value={formData.last_name || ''} onChange={e => handleFormChange('last_name', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Qualifications</label>
                      <input value={formData.qualifications || ''} onChange={e => handleFormChange('qualifications', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500" placeholder="e.g. BVSc MRCVS DACVS" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Clinics</label>
                    {/* Linked clinic tags */}
                    <div className="flex flex-wrap gap-1.5 mb-2 min-h-[1.5rem]">
                      {(formData.clinic_ids || []).length === 0 && <span className="text-xs text-gray-400 italic">No clinics linked</span>}
                      {(formData.clinic_ids || []).map(cid => {
                        const c = clinicsAll.find(x => x.id === cid);
                        return (
                          <span key={cid} className="inline-flex items-center space-x-1 px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded-full border border-green-200">
                            <span>{c ? c.name : cid}</span>
                            <button type="button" onClick={() => toggleClinicInForm(cid)} className="ml-0.5 text-green-500 hover:text-green-800">
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                    {/* Search to add clinics */}
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                      <input
                        type="text"
                        value={clinicSearch}
                        onChange={e => setClinicSearch(e.target.value)}
                        placeholder="Search clinics to add…"
                        className="w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                    {clinicSearch.trim() && (
                      <div className="mt-1 border border-gray-200 rounded-lg overflow-hidden max-h-44 overflow-y-auto">
                        {clinicsAll
                          .filter(c => c.name.toLowerCase().includes(clinicSearch.toLowerCase()) && !(formData.clinic_ids || []).includes(c.id))
                          .slice(0, 20)
                          .map(c => (
                            <button key={c.id} type="button"
                              onClick={() => { toggleClinicInForm(c.id); setClinicSearch(''); }}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-green-50 border-b border-gray-100 last:border-0">
                              <span className="font-medium text-gray-800">{c.name}</span>
                            </button>
                          ))}
                        {clinicsAll.filter(c => c.name.toLowerCase().includes(clinicSearch.toLowerCase()) && !(formData.clinic_ids || []).includes(c.id)).length === 0 && (
                          <div className="px-3 py-2 text-xs text-gray-400">No clinics found</div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* ── Contacts: grouped by context ── */}
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700">Contacts &amp; Emails</label>

                    {/* Personal / mobile phones */}
                    {(() => {
                      const phones = (formData.contacts || []).filter(c => c.type === 'phone');
                      return (
                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                          <div className="flex items-center justify-between px-3 py-1.5 bg-gray-50 border-b border-gray-200">
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1"><Phone className="h-3 w-3" /> Personal / Mobile</span>
                            <button type="button" onClick={() => setFormData(prev => ({ ...prev, contacts: [...(prev.contacts||[]), { id:`new-${Date.now()}`, type:'phone', value:'', context:'mobile', clinic_id:null, ownership_id:null, marketing_opt_in:true }] }))} className="text-xs text-green-600 hover:text-green-800 flex items-center gap-0.5"><Plus className="h-3 w-3"/>Add</button>
                          </div>
                          <div className="px-3 py-1 space-y-0.5 min-h-[1.5rem]">
                            {phones.length === 0 && <p className="text-xs text-gray-400 py-1">No personal phone numbers</p>}
                            {phones.map(c => (
                              <div key={c.id} className="flex items-center gap-2 py-0.5 border-b border-gray-100 last:border-0">
                                <Phone className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                                <input value={c.value} onChange={e => updateContact(c.id,'value',e.target.value)} placeholder="mobile / personal number" className="flex-1 text-xs border border-gray-200 rounded px-1.5 py-0.5 min-w-0" />
                                <button type="button" onClick={() => removeContact(c.id)} className="text-red-400 hover:text-red-600 shrink-0"><X className="h-3.5 w-3.5" /></button>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Read-only clinic phones */}
                    {(formData.clinic_ids || []).some(cid => clinicsAll.find(x => x.id === cid)?.phone) && (
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1"><Phone className="h-3 w-3" /> Clinic Phone Numbers <span className="font-normal normal-case text-gray-400 ml-1">(edit via Clinics tab)</span></span>
                        </div>
                        <div className="px-3 py-1 space-y-0.5">
                          {(formData.clinic_ids || []).map(cid => {
                            const clinic = clinicsAll.find(x => x.id === cid);
                            if (!clinic?.phone) return null;
                            return (
                              <div key={cid} className="flex items-center gap-2 py-0.5 border-b border-gray-100 last:border-0">
                                <Phone className="h-3.5 w-3.5 text-gray-300 shrink-0" />
                                <span className="text-xs text-gray-500 flex-1">{clinic.phone}</span>
                                <span className="text-xs text-gray-400 shrink-0 truncate max-w-[8rem]">{clinic.name}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Personal email */}
                    {(() => {
                      const personal = (formData.contacts || []).filter(c => c.type === 'email' && !c.clinic_id && !c.ownership_id);
                      return (
                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                          <div className="flex items-center justify-between px-3 py-1.5 bg-gray-50 border-b border-gray-200">
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1"><Mail className="h-3 w-3" /> Personal Email</span>
                            <button type="button" onClick={() => setFormData(prev => ({ ...prev, contacts: [...(prev.contacts||[]), { id:`new-${Date.now()}`, type:'email', value:'', context:'personal', clinic_id:null, ownership_id:null, marketing_opt_in:true }] }))} className="text-xs text-green-600 hover:text-green-800 flex items-center gap-0.5"><Plus className="h-3 w-3"/>Add</button>
                          </div>
                          <div className="px-3 py-1 space-y-0.5 min-h-[1.5rem]">
                            {personal.length === 0 && <p className="text-xs text-gray-400 py-1">No personal email</p>}
                            {personal.map(c => <ContactRow key={c.id} contact={c} editable={true} onUpdate={updateContact} onRemove={removeContact} />)}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Per-clinic emails */}
                    {(formData.clinic_ids || []).map(cid => {
                      const clinic = clinicsAll.find(x => x.id === cid);
                      const clinicEmail = clinic?.email || '';
                      const surgeonClinicEmails = (formData.contacts || []).filter(c => c.type === 'email' && c.clinic_id === cid);
                      const ownershipId = clinic?.ownership_id;
                      const ownerName = ownershipId ? ownership.find(o => o.id === ownershipId)?.name : null;
                      return (
                        <div key={cid} className="border border-green-200 rounded-lg overflow-hidden">
                          <div className="flex items-center justify-between px-3 py-1.5 bg-green-50 border-b border-green-200">
                            <span className="text-xs font-semibold text-green-800 flex items-center gap-1">
                              <Building2 className="h-3 w-3" />
                              {clinic ? clinic.name : cid}
                              {ownerName && <span className="font-normal text-green-600">· {ownerName}</span>}
                            </span>
                            <button type="button" onClick={() => addEmailForClinic(cid)} className="text-xs text-green-700 hover:text-green-900 flex items-center gap-0.5"><Plus className="h-3 w-3"/>Add email</button>
                          </div>
                          <div className="px-3 py-1 space-y-0.5 min-h-[1.5rem]">
                            {/* Clinic's own email — always read-only if present */}
                            {clinicEmail && (
                              <div className="flex items-center gap-2 py-1 border-b border-gray-100">
                                <Mail className="h-3.5 w-3.5 text-gray-300 shrink-0" />
                                <span className="flex-1 text-xs text-gray-500 truncate">{clinicEmail}</span>
                                <span className="text-xs text-gray-400 shrink-0 italic">clinic email</span>
                              </div>
                            )}
                            {/* Surgeon-specific emails for this clinic — always editable */}
                            {surgeonClinicEmails.length === 0 && !clinicEmail && <p className="text-xs text-gray-400 py-1">No email — add a surgeon-specific address</p>}
                            {surgeonClinicEmails.map(c => <ContactRow key={c.id} contact={c} editable={true} onUpdate={updateContact} onRemove={removeContact} />)}
                          </div>
                        </div>
                      );
                    })}

                    {/* Per-ownership-group emails (deduplicated) */}
                    {(() => {
                      const linkedOwnerIds = [...new Set(
                        (formData.clinic_ids || [])
                          .map(cid => clinicsAll.find(x => x.id === cid)?.ownership_id)
                          .filter(Boolean)
                      )];
                      if (linkedOwnerIds.length === 0) return null;
                      return linkedOwnerIds.map(oid => {
                        const own = ownership.find(o => o.id === oid);
                        const ownEmails = (formData.contacts || []).filter(c => c.type === 'email' && c.ownership_id === oid);
                        const clinicsUnderOwner = (formData.clinic_ids || []).filter(cid => clinicsAll.find(x => x.id === cid)?.ownership_id === oid);
                        return (
                          <div key={oid} className="border border-blue-200 rounded-lg overflow-hidden">
                            <div className="flex items-center justify-between px-3 py-1.5 bg-blue-50 border-b border-blue-200">
                              <span className="text-xs font-semibold text-blue-800 flex items-center gap-1">
                                <Landmark className="h-3 w-3" />
                                {own ? own.name : oid}
                                <span className="font-normal text-blue-500">— applies to {clinicsUnderOwner.length} clinic{clinicsUnderOwner.length !== 1 ? 's' : ''}</span>
                              </span>
                              <button type="button" onClick={() => addEmailForOwnership(oid)} className="text-xs text-blue-700 hover:text-blue-900 flex items-center gap-0.5"><Plus className="h-3 w-3"/>Add email</button>
                            </div>
                            <div className="px-3 py-1 space-y-0.5 min-h-[1.5rem]">
                              {ownEmails.length === 0 && <p className="text-xs text-gray-400 py-1">No ownership-level email</p>}
                              {ownEmails.map(c => <ContactRow key={c.id} contact={c} editable={true} onUpdate={updateContact} onRemove={removeContact} />)}
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea value={formData.notes || ''} onChange={e => handleFormChange('notes', e.target.value)} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500" />
                  </div>
                </>
              )}

              {/* CLINIC FORM */}
              {activeTab === 'clinics' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Clinic Name *</label>
                      <input value={formData.name || ''} onChange={e => handleFormChange('name', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Hubs</label>
                      <div className="border border-gray-200 rounded-lg p-2 max-h-36 overflow-y-auto space-y-1">
                        {hubs.map(h => (
                          <label key={h.id} className="flex items-center space-x-2 p-1.5 hover:bg-gray-50 rounded cursor-pointer">
                            <input type="checkbox"
                              checked={(formData.hub_ids || []).includes(h.id)}
                              onChange={() => {
                                const ids = formData.hub_ids || [];
                                handleFormChange('hub_ids', ids.includes(h.id) ? ids.filter(i => i !== h.id) : [...ids, h.id]);
                              }}
                              className="rounded text-purple-600" />
                            <span className="text-sm text-gray-700">{h.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Ownership</label>
                      <select value={formData.ownership_id || ''} onChange={e => handleFormChange('ownership_id', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500">
                        <option value="">— Unknown —</option>
                        {ownership.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Clinic Type</label>
                      <select value={formData.clinic_type || ''} onChange={e => handleFormChange('clinic_type', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500">
                        <option value="">— Unknown —</option>
                        {CLINIC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label>
                      <input value={formData.address_line1 || ''} onChange={e => handleFormChange('address_line1', e.target.value)} placeholder="Street / building" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
                      <input value={formData.address_line2 || ''} onChange={e => handleFormChange('address_line2', e.target.value)} placeholder="District / area (optional)" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">City / Town</label>
                      <input value={formData.address_city || ''} onChange={e => handleFormChange('address_city', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">County / Region</label>
                      <input value={formData.address_county || ''} onChange={e => handleFormChange('address_county', e.target.value)} placeholder="e.g. Hampshire" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
                      <input value={formData.postcode || ''} onChange={e => handleFormChange('postcode', e.target.value.toUpperCase())} placeholder="e.g. SW1A 1AA" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 font-mono" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <input value={formData.phone || ''} onChange={e => handleFormChange('phone', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input value={formData.email || ''} onChange={e => handleFormChange('email', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                      <input value={formData.website || ''} onChange={e => handleFormChange('website', e.target.value)} placeholder="https://www.example.com" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500" />
                    </div>
                  </div>
                </>
              )}

              {/* OWNERSHIP FORM */}
              {activeTab === 'ownership' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                      <input value={formData.name || ''} onChange={e => handleFormChange('name', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500" placeholder="e.g. CVS Group" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                      <select value={formData.type || 'corporate'} onChange={e => handleFormChange('type', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500">
                        <option value="independent">Independent</option>
                        <option value="corporate">Corporate</option>
                        <option value="charity">Charity</option>
                        <option value="unknown">Unknown</option>
                      </select>
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label>
                      <input value={formData.address_line1 || ''} onChange={e => handleFormChange('address_line1', e.target.value)} placeholder="Street / building" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
                      <input value={formData.address_line2 || ''} onChange={e => handleFormChange('address_line2', e.target.value)} placeholder="District / area (optional)" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">City / Town</label>
                      <input value={formData.address_city || ''} onChange={e => handleFormChange('address_city', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">County / Region</label>
                      <input value={formData.address_county || ''} onChange={e => handleFormChange('address_county', e.target.value)} placeholder="e.g. Hampshire" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
                      <input value={formData.postcode || ''} onChange={e => handleFormChange('postcode', e.target.value.toUpperCase())} placeholder="e.g. SW1A 1AA" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 font-mono" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                      <input value={formData.website || ''} onChange={e => handleFormChange('website', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500" placeholder="https://" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                      <textarea value={formData.notes || ''} onChange={e => handleFormChange('notes', e.target.value)} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500" />
                    </div>
                  </div>
                </>
              )}

              {/* HUB FORM */}
              {activeTab === 'hubs' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Hub Name *</label>
                      <input value={formData.name || ''} onChange={e => handleFormChange('name', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                      <input value={formData.address || ''} onChange={e => handleFormChange('address', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                      <input value={formData.website || ''} onChange={e => handleFormChange('website', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500" placeholder="https://" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                      <textarea value={formData.notes || ''} onChange={e => handleFormChange('notes', e.target.value)} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500" />
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 shrink-0">
              <button onClick={() => { setShowAddModal(false); setShowEditModal(false); setFormData({}); }} className="px-5 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium text-sm">Cancel</button>
              <button onClick={showEditModal ? handleSave : handleAdd} className="px-5 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium text-sm flex items-center space-x-2">
                <Check className="h-4 w-4" /><span>{showEditModal ? 'Save Changes' : `Add ${getSingularLabel(activeTab)}`}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDeleteModal
        confirmDelete={confirmDelete}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={handleDelete}
      />

      {showDupeModal && (
        <DuplicateFinderModal
          dupeMode={dupeMode}
          setDupeMode={setDupeMode}
          dupeRadius={dupeRadius}
          setDupeRadius={setDupeRadius}
          dupeGroups={dupeGroups}
          dupeLoading={dupeLoading}
          onSearch={findDuplicates}
          onClose={() => setShowDupeModal(false)}
          onMerge={openMerge}
        />
      )}

      <MergeClinicModal
        mergeTarget={mergeTarget}
        mergeForm={mergeForm}
        setMergeForm={setMergeForm}
        onCancel={() => setMergeTarget(null)}
        onMerge={handleMerge}
      />

      {showSurgeonDupeModal && (
        <SurgeonDuplicateFinderModal
          dupeGroups={surgeonDupeGroups}
          dupeLoading={surgeonDupeLoading}
          onSearch={findSurgeonDuplicates}
          onClose={() => setShowSurgeonDupeModal(false)}
          onMerge={openSurgeonMerge}
        />
      )}

      <SurgeonMergeModal
        mergeTarget={surgeonMergeTarget}
        mergeForm={surgeonMergeForm}
        setMergeForm={setSurgeonMergeForm}
        onCancel={() => setSurgeonMergeTarget(null)}
        onMerge={handleSurgeonMerge}
      />

    </div>
  );
};

export default SurgeonsDatabase;
